import Timestampable from '@/models/Timestampable'
import Harvest from '@/models/Harvest'
import {
  PlantInterface,
  GardenLocationInterface,
  PlantTypeInterface,
  PlantHarvestAggregateInterface,
  id,
  dateOnly,
  MetricInterface,
} from '@/types/models'

import { firestoreTimestampToDate } from '@/firebase'
import LogItem from './LogItem'
import Image from './Image'

// import { getStorageUrlSize } from '@/helpers/paths'

import store from '@/store'
import Garden from '@/models/Garden'
import Avatar from '@/models/Avatar'
import Metric from './Metric'
import {
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore'

interface HarvestAggregate {
  prop: keyof PlantHarvestAggregateInterface
  label: string
}

export default class Plant extends Timestampable {
  id: id
  gardenId: id
  name: string
  abbr: string
  description?: string
  startDate: dateOnly
  endDate: dateOnly | null
  location: GardenLocationInterface
  locationHistory?: GardenLocationInterface[]
  type?: PlantTypeInterface
  harvestAggregate?: PlantHarvestAggregateInterface
  metrics?: Metric<Plant>[]

  avatar?: Avatar // storageUrl | null = null;

  collections() {
    return {
      'harvests/bind': [this.gardenId, this.id],
    }
  }

  unsubscribers: Array<() => void> = []

  constructor(input: PlantInterface) {
    super(input)

    this.id = input.id
    this.gardenId = input.gardenId
    this.name = input.name
    this.abbr = input.abbr
    this.description = input.description
    this.startDate = input.startDate
    this.endDate = input.endDate
    this.location = input.location
    this.locationHistory = input.locationHistory
    this.type = input.type
    this.avatar = input.avatar
    this.harvestAggregate = input.harvestAggregate

    this.metrics = input.metrics // (input.metrics || []).map(metric => Metric.fromFirestore<Plant>(metric, this));
  }

  get garden(): Garden {
    return store.getters['gardens/find'](this.gardenId)
  }

  get harvests(): Harvest[] {
    return store.getters['gardens/plants/harvests/plantHarvests'](this.id)
  }

  get log(): LogItem[] {
    return store.getters['gardens/log/plantLog'](this.gardenId, this.id)
  }

  get images(): Image[] {
    return store.getters['gardens/images/plantImages'](
      this.gardenId,
      this.id,
      true
    )
  }

  get allImages(): Image[] {
    return store.getters['gardens/images/plantImages'](this.gardenId, this.id)
  }

  get harvestCount(): number {
    return this.harvestAggregate?.totalQuantity || 0 // this.harvests.reduce((prev, h) => prev + (h.quality > 0 ? h.quantity : 0), 0)
  }

  get hasHarvests(): boolean {
    return this.harvestCount > 0
  }

  get isActive(): boolean {
    return !this.endDate
  }

  get hasAvatar(): boolean {
    return !!this.avatar && this.avatar.isValid
  }

  get hasHarvestAggregate(): boolean {
    return (
      !!this.harvestAggregate &&
      Object.keys(this.harvestAggregate).length > 0 &&
      Object.values(this.harvestAggregate).some((v) => v !== 0)
    )
  }

  // get avatarThumbnail(): string | null {
  //   if (!this.hasAvatar) return null

  //   return getStorageUrlSize(this.avatar || '', 40)
  // }

  // get avatarSquare(): string | null {
  //   if (!this.hasAvatar) return null

  //   return getStorageUrlSize(this.avatar || '', 200)
  // }

  // get largeAvatar(): string | null {
  //   if (!this.hasAvatar) return null

  //   return getStorageUrlSize(this.avatar || '', 200)
  // }

  public static harvestAggregate: HarvestAggregate[] = [
    { prop: 'avgQuality', label: 'Quality' },
    { prop: 'avgRipeness', label: 'Ripe' },
    { prop: 'avgSize', label: 'Size' },
  ]

  static fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
    gardenId: id
  ): Plant {
    const data = snapshot.data(options)

    const result: PlantInterface = {
      id: snapshot.id,
      gardenId: gardenId,
      name: data.name,
      abbr: data.abbr,
      description: data.description,
      quantity: data.quantity,
      startDate: data.startDate,
      endDate: data.endDate,
      location: data.location,
      locationHistory: data.locationHistory,
      type: data.type,
      harvestAggregate: data.harvestAggregate,
      avatar: Avatar.fromFirestoreProp(data.avatar, snapshot.id),
      metrics: (data.metrics || []).map((metric: MetricInterface<Plant>) =>
        Metric.fromFirestore<Plant>(metric)
      ),

      createdBy: data.createdBy,
      createdAt: firestoreTimestampToDate(data.createdAt),
      updatedBy: data.updatedBy,
      updatedAt: firestoreTimestampToDate(data.updatedAt),
    }

    return new Plant(result)
  }

  static firestoreConverter = (gardenId: id) => ({
    toFirestore(plant: Plant): DocumentData {
      return {
        // TODO: implement toFirestore for Plant class
        name: plant.name,
        abbr: plant.abbr,
      }
    },
    fromFirestore(
      snapshot: QueryDocumentSnapshot,
      options: SnapshotOptions
    ): Plant {
      return Plant.fromFirestore(snapshot, options, gardenId)
    },
  })
}
